import React from "react";
import { formatTime } from "../Utilities/time";

export function PlayPauseButton(props) {
  var playing = props.playing;
  return (
    <button
      type="button"
      className="ngp-btn"
      aria-label={playing ? "Pause" : "Play"}
      onClick={props.onClick}
      data-ngp-focus={props["data-ngp-focus"]}
      data-ngp-focus-group={props["data-ngp-focus-group"]}
    >
      {playing ? "❚❚" : "▶"}
    </button>
  );
}

export function SkipButton(props) {
  if (props.hidden) {
    return null;
  }
  return (
    <button
      type="button"
      className="ngp-btn"
      aria-label={props.label}
      onClick={props.onClick}
      data-ngp-focus={props["data-ngp-focus"]}
      data-ngp-focus-group={props["data-ngp-focus-group"]}
    >
      {props.children}
    </button>
  );
}

export function VolumeControl(props) {
  var fa = props.focusAttrs || function () {
    return {};
  };
  var muteFocus = fa("mute");
  var volFocus = fa("volume");
  return (
    <span className="ngp-volume">
      <button
        type="button"
        className="ngp-btn"
        aria-label={props.muted ? "Unmute" : "Mute"}
        data-ngp-mute="1"
        onClick={props.onToggleMute}
        data-ngp-focus={muteFocus["data-ngp-focus"]}
        data-ngp-focus-group={muteFocus["data-ngp-focus-group"]}
      >
        {props.muted || props.volume === 0 ? "🔇" : "🔊"}
      </button>
      <input
        type="range"
        min="0"
        max="1"
        step="0.05"
        value={props.muted ? 0 : props.volume}
        onChange={function (e) {
          props.onVolumeChange(Number(e.target.value));
        }}
        aria-label="Volume"
        data-ngp-focus={volFocus["data-ngp-focus"]}
        data-ngp-focus-group={volFocus["data-ngp-focus-group"]}
      />
    </span>
  );
}

export function SeekBar(props) {
  var duration = props.duration || 0;
  var current = props.currentTime || 0;
  var buffered = props.bufferedEnd || 0;
  var pct = duration ? (current / duration) * 100 : 0;
  var bufPct = duration ? (buffered / duration) * 100 : 0;
  var chapters = props.chapters || [];
  var fa = props.focusAttrs || function () {
    return {};
  };
  var seekFocus = fa("seek");

  return (
    <div className="ngp-seek">
      <span className="ngp-time" aria-hidden="true">
        {formatTime(current)}
      </span>
      <div className="ngp-seek-track">
        <div className="ngp-seek-buffer" style={{ width: bufPct + "%" }} />
        {chapters.map(function (ch) {
          if (!duration) {
            return null;
          }
          var left = (ch.start / duration) * 100;
          return (
            <button
              key={ch.id}
              type="button"
              className="ngp-chapter-mark"
              style={{ left: left + "%" }}
              title={ch.title}
              aria-label={"Chapter " + ch.title}
              onClick={function (e) {
                e.preventDefault();
                e.stopPropagation();
                if (props.onChapterSelect) {
                  props.onChapterSelect(ch.id);
                }
              }}
            />
          );
        })}
        <input
          type="range"
          min="0"
          max={duration || 0}
          step="0.1"
          value={current}
          disabled={!duration}
          onChange={function (e) {
            props.onSeek(Number(e.target.value));
          }}
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={Math.floor(duration || 0)}
          aria-valuenow={Math.floor(current || 0)}
          aria-valuetext={formatTime(current) + " of " + formatTime(duration)}
          style={{ backgroundSize: pct + "% 100%" }}
          data-ngp-focus={seekFocus["data-ngp-focus"]}
          data-ngp-focus-group={seekFocus["data-ngp-focus-group"]}
        />
      </div>
      <span className="ngp-time" aria-hidden="true">
        {formatTime(duration)}
      </span>
    </div>
  );
}

export function SpeedControl(props) {
  var rates = props.rates || [0.5, 0.75, 1, 1.25, 1.5, 2];
  return (
    <label className="ngp-speed">
      <span className="ngp-sr-only">Playback speed</span>
      <select
        value={String(props.rate)}
        onChange={function (e) {
          props.onChange(Number(e.target.value));
        }}
        aria-label="Playback speed"
        data-ngp-focus={props["data-ngp-focus"]}
        data-ngp-focus-group={props["data-ngp-focus-group"]}
      >
        {rates.map(function (r) {
          return (
            <option key={r} value={String(r)}>
              {r}x
            </option>
          );
        })}
      </select>
    </label>
  );
}

export function QualityControl(props) {
  var quality = props.quality || {};
  var levels = quality.levels || [];
  if (!props.enabled || !levels.length) {
    return null;
  }
  var value =
    quality.mode === "auto" || quality.currentLevel === -1
      ? "-1"
      : String(quality.currentLevel);

  return (
    <label className="ngp-quality">
      <span className="ngp-sr-only">Quality</span>
      <select
        value={value}
        onChange={function (e) {
          props.onChange(Number(e.target.value));
        }}
        aria-label="Quality"
        data-ngp-focus={props["data-ngp-focus"]}
        data-ngp-focus-group={props["data-ngp-focus-group"]}
      >
        <option value="-1">Auto</option>
        {levels.map(function (lvl) {
          return (
            <option key={lvl.index} value={String(lvl.index)}>
              {lvl.name || lvl.height + "p"}
            </option>
          );
        })}
      </select>
    </label>
  );
}

export function TextTrackControl(props) {
  var tracks = props.tracks || [];
  if (!props.enabled) {
    return null;
  }
  var hasTracks = tracks.length > 0;
  var value = hasTracks && props.activeId ? props.activeId : "off";
  return (
    <label className="ngp-text-track">
      <span className="ngp-sr-only">Subtitles</span>
      <select
        value={value}
        onChange={function (e) {
          props.onChange(e.target.value);
        }}
        aria-label="Subtitles"
        data-ngp-focus={props["data-ngp-focus"]}
        data-ngp-focus-group={props["data-ngp-focus-group"]}
      >
        <option value="off">Subtitles off</option>
        {hasTracks
          ? tracks.map(function (t) {
              return (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              );
            })
          : // Always selectable: On with no catalog → toast (no fake cues).
            [
              <option key="on" value="on">
                Subtitles on
              </option>,
            ]}
      </select>
    </label>
  );
}

export function AudioTrackControl(props) {
  var tracks = props.tracks || [];
  if (!props.enabled || tracks.length < 2) {
    return null;
  }
  return (
    <label className="ngp-audio-track">
      <span className="ngp-sr-only">Audio track</span>
      <select
        value={props.activeId || (tracks[0] && tracks[0].id) || ""}
        onChange={function (e) {
          props.onChange(e.target.value);
        }}
        aria-label="Audio track"
        data-ngp-focus={props["data-ngp-focus"]}
        data-ngp-focus-group={props["data-ngp-focus-group"]}
      >
        {tracks.map(function (t) {
          return (
            <option key={t.id} value={t.id}>
              {t.label}
              {t.kind && t.kind !== "main" ? " (" + t.kind + ")" : ""}
            </option>
          );
        })}
      </select>
    </label>
  );
}

export function TheaterModeButton(props) {
  if (props.hidden) {
    return null;
  }
  var on = !!props.active;
  return (
    <button
      type="button"
      className={"ngp-btn" + (on ? " ngp-btn--active" : "")}
      aria-label={on ? "Exit theater mode" : "Theater mode"}
      aria-pressed={on ? "true" : "false"}
      onClick={props.onClick}
      data-ngp-focus={props["data-ngp-focus"]}
      data-ngp-focus-group={props["data-ngp-focus-group"]}
      title={on ? "Exit theater mode" : "Theater mode"}
    >
      ▤
    </button>
  );
}

export function FullscreenButton(props) {
  if (props.hidden) {
    return null;
  }
  return (
    <button
      type="button"
      className="ngp-btn"
      aria-label="Fullscreen"
      onClick={props.onClick}
      data-ngp-focus={props["data-ngp-focus"]}
      data-ngp-focus-group={props["data-ngp-focus-group"]}
    >
      ⛶
    </button>
  );
}

export function PiPButton(props) {
  if (props.hidden) {
    return null;
  }
  return (
    <button
      type="button"
      className="ngp-btn"
      aria-label="Picture in picture"
      onClick={props.onClick}
      data-ngp-focus={props["data-ngp-focus"]}
      data-ngp-focus-group={props["data-ngp-focus-group"]}
    >
      PiP
    </button>
  );
}

export function CastButton(props) {
  if (props.hidden) {
    return null;
  }
  var casting = !!props.casting;
  var label = casting ? "Stop casting" : "Cast";
  return (
    <button
      type="button"
      className={
        "ngp-btn ngp-btn-cast" + (casting ? " ngp-btn-cast--active" : "")
      }
      aria-label={label}
      aria-pressed={casting ? "true" : "false"}
      data-ngp-focus="cast"
      data-ngp-focus-group="controls"
      onClick={props.onClick}
      title={props.title || label}
    >
      {casting ? "◉ Cast" : "Cast"}
    </button>
  );
}

/**
 * PiP visibility follows the *active* surface after intro→main remount.
 *
 * Element PiP only works for HTMLVideoElement surfaces (progressive / HLS).
 * Embed providers (YouTube / Vimeo) are never shown — capability matrix.
 *
 * Do not require caps.supportsPiP === true (idle/default false caused the
 * button to vanish after handoff before/while the main provider bound).
 */
function shouldShowPiPButton(st) {
  if (typeof document === "undefined" || !document.pictureInPictureEnabled) {
    return false;
  }
  var caps = st && st.capabilities;
  var providerName =
    (st && st.providerName) || (caps && caps.providerName) || null;
  var surfaceKind =
    (st && st.surfaceKind) || (caps && caps.surfaceKind) || null;

  if (
    surfaceKind === "embed" ||
    providerName === "youtube-embed" ||
    providerName === "vimeo-embed"
  ) {
    return false;
  }
  // Video surface, progressive/hls, or not-yet-bound: show
  return true;
}

export function ControlBar(props) {
  var st = props.state || {};
  var features = props.features || {};
  var playing =
    st.playbackState === "playing" || st.playbackState === "buffering";
  var skip = features.skipInterval != null ? features.skipInterval : 10;
  var hidden = props.controlsVisible === false;
  var tv = !!features.tvNavigation;
  var focusAttrs = function (id) {
    return tv
      ? { "data-ngp-focus": id, "data-ngp-focus-group": "controls" }
      : {};
  };

  return (
    <div
      className={
        "ngp-controls" +
        (hidden ? " ngp-controls--hidden" : "") +
        (tv ? " ngp-controls--tv" : "")
      }
      style={props.style}
      aria-hidden={hidden ? "true" : "false"}
    >
      <div className="ngp-controls-row">
        <PlayPauseButton
          playing={playing}
          onClick={props.onPlayPause}
          {...focusAttrs("play")}
        />
        {features.skipButtons !== false ? (
          <React.Fragment>
            <SkipButton
              label={"Skip back " + skip + " seconds"}
              onClick={function () {
                props.onSkip(-skip);
              }}
              {...focusAttrs("skip-back")}
            >
              -{skip}s
            </SkipButton>
            <SkipButton
              label={"Skip forward " + skip + " seconds"}
              onClick={function () {
                props.onSkip(skip);
              }}
              {...focusAttrs("skip-fwd")}
            >
              +{skip}s
            </SkipButton>
          </React.Fragment>
        ) : null}
        <VolumeControl
          volume={st.volume}
          muted={st.muted}
          onToggleMute={props.onToggleMute}
          onVolumeChange={props.onVolumeChange}
          focusAttrs={focusAttrs}
        />
        <SeekBar
          currentTime={st.currentTime}
          duration={st.duration}
          bufferedEnd={st.bufferedEnd}
          chapters={props.chapters}
          onSeek={props.onSeek}
          onChapterSelect={props.onChapterSelect}
          focusAttrs={focusAttrs}
        />
        {features.playbackSpeed !== false ? (
          <SpeedControl
            rate={st.playbackRate}
            onChange={props.onRateChange}
            {...focusAttrs("speed")}
          />
        ) : null}
        {features.qualitySelection !== false ? (
          <QualityControl
            enabled={true}
            quality={st.quality}
            onChange={props.onQualityChange}
            {...focusAttrs("quality")}
          />
        ) : null}
        {features.subtitles !== false ? (
          <TextTrackControl
            enabled={true}
            tracks={props.textTracks}
            activeId={props.activeTextId}
            onChange={props.onTextTrackChange}
            {...focusAttrs("subs")}
          />
        ) : null}
        {features.audioTracks !== false ? (
          <AudioTrackControl
            enabled={true}
            tracks={props.audioTracks}
            activeId={props.activeAudioId}
            onChange={props.onAudioTrackChange}
            {...focusAttrs("audio")}
          />
        ) : null}
        {features.casting ? (
          <CastButton
            casting={!!props.castActive}
            onClick={props.onCast}
            title={props.castTitle}
          />
        ) : null}
        {features.pictureInPicture !== false &&
        shouldShowPiPButton(st) ? (
          <PiPButton onClick={props.onPiP} {...focusAttrs("pip")} />
        ) : null}
        {features.theaterMode !== false ? (
          <TheaterModeButton
            active={!!props.theaterMode}
            onClick={props.onTheater}
            {...focusAttrs("theater")}
          />
        ) : null}
        {features.fullscreen !== false ? (
          <FullscreenButton
            onClick={props.onFullscreen}
            {...focusAttrs("fullscreen")}
          />
        ) : null}
      </div>
      {st.castTarget === "remote" ? (
        <div className="ngp-cast-banner" aria-live="polite">
          Casting
          {props.castProviderName ? " · " + props.castProviderName : ""}
        </div>
      ) : null}
    </div>
  );
}

export default ControlBar;
